import {usePlantStartOfToday, useUpmPlantTimezone} from '@hconnect/upm'
import moment from 'moment-timezone'
import {useMemo, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useMaterialDemandFilters, useSelectedTimeframe} from '../providers'
import {type MomentRange} from '../utils'

import {TimeFrameSelect, type TimeFrameSelectOption} from './TimeFrameSelect'

export const getSalesForecastDefaultTimeFrame = (startOfToday: moment.Moment): MomentRange => {
  return [startOfToday.clone().subtract(1, 'month'), startOfToday.clone().add(2, 'weeks')]
}

export const SalesForecastTimeFrameSelect = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()
  const {selectedTimeframe, setSelectedTimeframe} = useSelectedTimeframe()
  if (!selectedTimeframe) {
    throw new Error('BUG: timeFrame should be selected for this component')
  }

  const {selectedDate, clearSelectedDate} = useMaterialDemandFilters()
  const startOfToday = usePlantStartOfToday()

  const options = useMemo<TimeFrameSelectOption[]>(
    () => [
      {
        label: t('common.timeFrame.pastMonthNext2Weeks'),
        timeFrame: getSalesForecastDefaultTimeFrame(startOfToday)
      },
      {
        label: t('common.timeFrame.lastWeek'),
        timeFrame: [startOfToday.clone().subtract(1, 'week'), startOfToday]
      },
      {
        label: t('common.timeFrame.lastMonth'),
        timeFrame: [startOfToday.clone().subtract(1, 'month'), startOfToday]
      },
      {
        label: t('common.timeFrame.last3Months'),
        timeFrame: [startOfToday.clone().subtract(3, 'months'), startOfToday]
      },
      {
        label: t('common.timeFrame.ytd'),
        timeFrame: [startOfToday.clone().startOf('year'), startOfToday]
      },
      {
        label: t('common.timeFrame.currentWeek'),
        timeFrame: [
          startOfToday.clone().startOf('week'),
          startOfToday.clone().startOf('week').add(1, 'week')
        ]
      },
      {
        label: t('common.timeFrame.currentMonth'),
        timeFrame: [
          startOfToday.clone().startOf('month'),
          startOfToday.clone().startOf('month').add(1, 'month')
        ]
      },
      {
        label: t('common.timeFrame.currentYear'),
        timeFrame: [
          startOfToday.clone().startOf('year'),
          startOfToday.clone().startOf('year').add(1, 'year')
        ]
      },
      {
        label: t('common.timeFrame.next3Months'),
        timeFrame: [startOfToday, startOfToday.clone().add(3, 'months')]
      },
      {
        label: t('common.timeFrame.untilYearEnd'),
        timeFrame: [startOfToday, startOfToday.clone().add(1, 'year').startOf('year')]
      },
      {
        label: t('common.timeFrame.nextYear'),
        timeFrame: [
          startOfToday.clone().startOf('year').add(1, 'year'),
          startOfToday.clone().startOf('year').add(2, 'years')
        ]
      }
    ],
    [startOfToday, t]
  )

  const handleTimeframeChange = useCallback(
    (timeframe: MomentRange) => {
      // clearing demand filters if selected date is not in the new timeframe
      if (!moment.tz(selectedDate, timezoneId).isBetween(timeframe[0], timeframe[1])) {
        clearSelectedDate()
      }
      setSelectedTimeframe(timeframe)
    },
    [timezoneId, selectedDate, clearSelectedDate, setSelectedTimeframe]
  )

  return (
    <TimeFrameSelect
      timeFrame={selectedTimeframe}
      options={options}
      onChange={handleTimeframeChange}
      backgroundMode="onLight"
      label={t('common.selectTimeframe')}
    />
  )
}
